/*
  HILO lead guard — shared by the booking widget, the stylist popup and the
  Talk-to-stylist page (Phase 2A, Oct 2026).

  check(opts)  Cheap spam screens that run in the visitor's browser:
               - honeypot: a hidden field people never see; form bots fill it.
               - time trap: a form sent less than 2.5 s after it opened.
               - per-browser limit: 3 sent leads per 10 minutes, and the same
                 number on the same form within 2 minutes counts as a repeat.
               These stop form-filling bots, double taps and accidental floods.
               They cannot stop someone posting straight to a webhook address;
               only a server-side relay can do that.

  send(targets, opts)
               Posts the lead and resolves only once delivery is confirmed:
               - Supabase intake answers {ok:true,id}  -> confirmed (it is the CRM)
               - otherwise a readable 2xx from Wylto   -> confirmed
               - otherwise not confirmed, so the form can say so and offer
                 WhatsApp / call instead of a false "you're all set".
               Pass {secondary:false} on a retry so Wylto and the Sheet are not
               sent the same lead twice.

  record(form, phone)
               Notes a confirmed lead for the per-browser limit. Only a short
               hash of the number is stored, never the number itself.
*/
(function () {
  if (window.hiloLead) return;

  var KEY = 'hilo_lead_log';
  var WINDOW_MS = 10 * 60 * 1000, MAX_PER_WINDOW = 3, MIN_FILL_MS = 2500, REPEAT_MS = 2 * 60 * 1000, TIMEOUT_MS = 15000;

  var mem = [];
  var canStore = (function () {
    try { localStorage.setItem(KEY + '_t', '1'); localStorage.removeItem(KEY + '_t'); return true; } catch (e) { return false; }
  })();

  function now() { return Date.now(); }
  function load() {
    if (!canStore) return mem.slice();
    try { var a = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(a) ? a : []; } catch (e) { return []; }
  }
  function save(a) {
    a = a.slice(-20);
    if (!canStore) { mem = a; return; }
    try { localStorage.setItem(KEY, JSON.stringify(a)); } catch (e) { mem = a; }
  }
  /* FNV-1a over the last 10 digits: enough to spot a repeat, useless as a phone number. */
  function phoneKey(p) {
    var d = String(p || '').replace(/\D/g, '').slice(-10), h = 2166136261;
    if (!d) return '';
    for (var i = 0; i < d.length; i++) { h ^= d.charCodeAt(i); h = (h * 16777619) >>> 0; }
    return h.toString(36);
  }
  function recent() {
    var t = now();
    return load().filter(function (e) { return e && typeof e.t === 'number' && t - e.t < WINDOW_MS && e.t <= t + 60000; });
  }

  function check(o) {
    o = o || {};
    var hp = o.honeypot, hv = hp && typeof hp === 'object' ? hp.value : hp;
    if (hv && String(hv).trim()) return { ok: false, reason: 'honeypot', silent: true };
    if (o.startedAt && now() - o.startedAt < MIN_FILL_MS) {
      return { ok: false, reason: 'too_fast', msg: 'Please check your details, then tap the button again.' };
    }
    var log = recent(), pk = phoneKey(o.phone), t = now();
    if (!o.repeatOk && pk) {
      for (var i = log.length - 1; i >= 0; i--) {
        if (log[i].f === o.form && log[i].p === pk && t - log[i].t < REPEAT_MS) return { ok: false, reason: 'repeat', asSuccess: true };
      }
    }
    if (log.length >= MAX_PER_WINDOW) {
      return { ok: false, reason: 'rate_limited', msg: 'You have sent a few requests in the last few minutes. Please wait a little, or message us on WhatsApp.' };
    }
    return { ok: true };
  }

  function record(form, phone) {
    var a = recent();
    a.push({ t: now(), f: form || '', p: phoneKey(phone) });
    save(a);
  }

  function timed(p) {
    return new Promise(function (resolve) {
      var done = false;
      var tm = setTimeout(function () { if (!done) { done = true; resolve({ ok: false, error: 'timeout' }); } }, TIMEOUT_MS);
      p.then(function (v) { if (!done) { done = true; clearTimeout(tm); resolve(v); } },
             function (e) { if (!done) { done = true; clearTimeout(tm); resolve({ ok: false, error: (e && e.message) || 'network' }); } });
    });
  }
  function bodyOf(b) { return typeof b === 'string' ? b : JSON.stringify(b || {}); }
  /* text/plain + no-cors: no pre-flight, always delivered, response unreadable. */
  function opaque(url, body) {
    try {
      return fetch(url, { method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: body, keepalive: true })
        .then(function () { return { ok: false, opaque: true }; }, function () { return { ok: false, error: 'network' }; });
    } catch (e) { return Promise.resolve({ ok: false, error: 'network' }); }
  }

  function toSupabase(t) {
    if (!t || !t.url) return Promise.resolve({ ok: false, error: 'no_url' });
    var req;
    try { req = fetch(t.url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: bodyOf(t.body) }); }
    catch (e) { return Promise.resolve({ ok: false, error: 'network' }); }
    return timed(req.then(function (r) {
      return r.json().then(function (j) {
        return { ok: !!(j && j.ok === true), id: (j && j.id) || '', reused: !!(j && j.reused), error: (j && j.error) || '', status: r.status };
      }, function () { return { ok: false, error: 'bad_response', status: r.status }; });
    }));
  }
  function toWylto(t) {
    if (!t || !t.url) return Promise.resolve({ ok: false, error: 'no_url' });
    var body = bodyOf(t.body), req;
    try { req = fetch(t.url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true }); }
    catch (e) { return opaque(t.url, body); }
    return timed(req.then(function (r) { return { ok: r.ok, status: r.status }; }, function () { return opaque(t.url, body); }));
  }
  function toSheet(t) { if (t && t.url) opaque(t.url, bodyOf(t.body)); }

  function send(targets, opts) {
    targets = targets || {}; opts = opts || {};
    var secondary = opts.secondary !== false;
    var s = toSupabase(targets.supabase);
    var w = secondary ? toWylto(targets.wylto) : Promise.resolve({ ok: false, error: 'skipped' });
    if (secondary) toSheet(targets.sheet);
    return s.then(function (sr) {
      if (sr.ok) return { ok: true, via: 'supabase', id: sr.id, reused: sr.reused };
      return w.then(function (wr) {
        if (wr.ok) return { ok: true, via: 'wylto', id: '', supabase_error: sr.error || '' };
        return { ok: false, via: 'none', error: sr.error || wr.error || 'unconfirmed' };
      });
    });
  }

  window.hiloLead = { check: check, record: record, send: send, version: '2a.1' };
})();
